import React from 'react';
import { Container, Row, Col, Table, Alert, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import InlineOptIn from '../../components/InlineOptIn';

const BlogPostCalfTwitchingALS = () => {
  return (
    <>
      {/* ========== SEO META TAGS ========== */}
      <Helmet>
        <title>Calf Twitching and ALS Fears: A Deep Dive on How to Tell the Difference</title>
        <meta
          name="description"
          content="An in-depth, 2000+ word definitive guide on distinguishing benign calf twitching from ALS fasciculations. Covers pathophysiology, the neuro exam, EMG results, and more to calm your fears."
        />
        <meta
          name="keywords"
          content="calf twitching ALS, calf fasciculations ALS, calf twitching vs als, benign fasciculation syndrome vs als, twitching and als, muscle twitching als, als twitching no weakness, als twitching location"
        />
        <link
          rel="canonical"
          href="https://benignfasciculationsyndrome.org/blog/calf-twitching-als"
        />
        <meta property="og:title" content="Calf Twitching and ALS Fears: A Deep Dive on How to Tell the Difference" />
        <meta property="og:description" content="The most comprehensive guide available for understanding the profound differences between benign fasciculations and ALS symptoms, grounded in neurology and designed to alleviate anxiety." />
        <meta property="og:url" content="https://benignfasciculationsyndrome.org/blog/calf-twitching-als" />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />

        {/* Article Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'Calf Twitching and ALS Fears: A Deep Dive on How to Tell the Difference',
            description: 'A comprehensive, 2000+ word article explaining the critical differences between benign calf twitching and the fasciculations seen in ALS, focusing on clinical weakness, pathophysiology, and the neurological exam.',
            datePublished: '2025-09-16',
            author: {
              '@type': 'Organization',
              name: 'Benign Fasciculation Syndrome',
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': 'https://benignfasciculationsyndrome.org/blog/calf-twitching-als',
            },
          })}
        </script>

        {/* FAQ Schema for Rich Results */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'Is calf twitching the first sign of ALS?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'No, widespread calf twitching in the absence of clinical weakness is almost never the first sign of ALS. The initial symptoms of ALS are typically progressive muscle weakness, such as foot drop (inability to lift the front of the foot) or a weakened hand grip. Fasciculations in ALS are a secondary phenomenon that occurs in muscles that are already weak and showing signs of atrophy (wasting).',
                },
              },
              {
                '@type': 'Question',
                name: 'What is the main difference between BFS and ALS twitching?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'The main difference is the context. In Benign Fasciculation Syndrome (BFS), twitching is the primary symptom occurring in strong, functional muscles. In ALS, twitching is a secondary symptom occurring in muscles that are already experiencing significant, progressive weakness and are failing. The twitch in BFS is a sign of a hyperexcitable nerve; the twitch in ALS is a sign of a dying nerve.',
                },
              },
              {
                '@type': 'Question',
                name: 'Can you have ALS with only twitching and no weakness?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'This scenario is exceptionally rare and contradicts the fundamental pathology of the disease. ALS is defined by the death of motor neurons, which causes clinical weakness. Fasciculations are a byproduct of this process. A diagnosis of ALS requires objective, measurable evidence of progressive clinical weakness found during a neurological examination.',
                },
              },
            ],
          })}
        </script>
      </Helmet>

      {/* ========== BLOG POST HERO BANNER ========== */}
      <div
        style={{
          backgroundColor: '#003f5c',
          color: '#fff',
          padding: '4rem 0',
          textAlign: 'center',
        }}
      >
        <Container>
          <p className="text-white-50">Cluster Article | September 16, 2025</p>
          <h1 style={{ fontWeight: '700', fontSize: '2.5rem', maxWidth: '900px', margin: '0 auto' }}>
            Calf Twitching and ALS Fears: A Deep Dive on How to Tell the Difference
          </h1>
          <p className="lead mt-3" style={{ fontSize: '1.25rem', fontWeight: '300', maxWidth: '700px', margin: '0 auto', opacity: '0.9' }}>
            This is the definitive, in-depth guide to understanding the profound, reassuring differences between a benign twitch and a symptom of serious disease.
          </p>
        </Container>
      </div>

      {/* ========== ARTICLE CONTENT ========== */}
      <Container style={{ maxWidth: '800px', padding: '4rem 1rem' }}>
        <Row>
          <Col>
            {/* Quick answer — featured-snippet / AI Overview bait */}
            <div className="p-4 mb-4 rounded" style={{ backgroundColor: '#eef4f7', borderLeft: '5px solid #003f5c' }}>
              <p className="mb-2"><strong>Quick answer:</strong> Calf twitching on its own is <strong>very rarely ALS</strong>. The difference that matters isn't the twitch — it's <strong>weakness</strong>:</p>
              <ul className="mb-2">
                <li><strong>Benign twitching (BFS):</strong> the twitch is the whole story — your leg still works normally. Usually stress, caffeine, exercise, or low magnesium.</li>
                <li><strong>ALS:</strong> real, measurable weakness comes first or alongside — you can't do things you used to, and the muscle visibly shrinks.</li>
              </ul>
              <p className="mb-0">In ALS, twitching appears in muscles that are <em>already weakening</em>. Twitching in a calf that's still strong points firmly away from ALS. See a doctor if you develop genuine weakness, foot drop, or muscle wasting.</p>
            </div>

            {/* Introduction */}
            <p className="lead">
              It began as a minor annoyance. A strange, rippling sensation in your calf while you were watching TV. You ignored it. But it didn't stop. Days turned into weeks, and the "popcorn" or "worms under the skin" feeling became a constant companion. You did what we all do: you searched for answers online. And in the cold glow of your screen, you fell down a digital rabbit hole and found three letters that changed everything: A-L-S.
            </p>
            <p>
              Suddenly, every twitch is no longer an annoyance; it's a potential death sentence. You're trapped in a terrifying feedback loop of fear, hypervigilance, and constant self-testing. You're here, reading this, because you are desperate for a definitive answer to the question that keeps you up at night: "Is this twitching in my calf benign, or is it the start of something terrible?"
            </p>
            <p>
              Take a deep breath. This is not another vague article. This is a deep, comprehensive dive into the neurology that separates a harmless twitch from a sinister one. We will explore the pathophysiology, the clinical exam, the psychology of fear, and the diagnostic tests in detail. Our goal is to replace your profound fear with profound understanding, grounded in medical science.
            </p>

            <Alert variant="info" className="my-4">
              <Alert.Heading as="h4">The Unbreakable Rule of Neurology</Alert.Heading>
              <p>
                If you absorb only one concept from this entire article, let it be this: <strong>Amyotrophic Lateral Sclerosis (ALS) is a disease of progressive, clinical weakness. It is not, and never has been, a disease of twitching.</strong> In the absence of true, measurable muscle failure, your fasciculations are not a sign of ALS. This is the cornerstone of diagnosis.
              </p>
            </Alert>

            <h2 className="mt-5 mb-3 fw-bold">Part 1: The Anatomy of a Twitch – Why the Calves?</h2>
            <p>
              Before diving into disease, it's crucial to understand why your calves are such a common hotspot. The calf muscles (primarily the gastrocnemius and soleus) are anatomical marvels. They are powerful, constantly engaged, and controlled by some of the longest nerves in your body, originating from your lower spine.
            </p>
            <ul>
              <li><strong>High Workload:</strong> Your calves work all day long, bearing your body's weight, making them prone to fatigue and metabolic stress.</li>
              <li><strong>Long Nerve Pathways:</strong> The sciatic nerve, the longest in the body, branches into the tibial and peroneal nerves to control the lower leg. This sheer length of the "axon" (the nerve's wire) creates more opportunity for a benign misfire to occur, much like a long power line is more susceptible to interference than a short one.</li>
              <li><strong>High Mineral Demand:</strong> These hard-working muscles have a high demand for electrolytes like magnesium, potassium, and calcium, which are essential for nerve stability and proper firing.</li>
            </ul>
            <p>
              This combination makes the calves a prime location for benign fasciculations. Their twitching is more often a sign of a tired, stressed, or under-fueled system than a diseased one. For a complete list of these triggers, please read our foundational{' '}
              <strong><Link to="/blog/calf-twitching-causes">Pillar Guide to Calf Twitching Causes</Link></strong>.
            </p>

            <h2 className="mt-5 mb-3 fw-bold">Part 2: The Science of the Signal – A Hyperexcitable Nerve vs. a Dying Nerve</h2>
            <p>
              The core of your fear lies in a single question: what is the fundamental difference between the twitch of BFS and the twitch of ALS? The answer lies in the health and behavior of the motor neuron at a microscopic level.
            </p>
            <h3 className="mt-4 fw-semibold">The Benign Twitch: Unstable Ion Channels in a Healthy Nerve</h3>
            <p>
              In Benign Fasciculation Syndrome (BFS), your motor neurons are structurally healthy and fully functional. The issue lies with the "ion channels" on the nerve's surface—tiny gates that control the flow of sodium and potassium to generate an electrical signal. In BFS, these gates are "leaky" or unstable.
            </p>
            <p>
              Think of it as a car alarm system that's been set to be too sensitive. Factors like stress (releasing adrenaline), caffeine, or lack of sleep can make these ion channels even more unstable, lowering the threshold needed to trigger a nerve impulse. The nerve can fire spontaneously without any command from the brain, causing a twitch. Crucially, when your brain *does* send a command—"flex your calf"—the signal travels perfectly, and the muscle responds with full strength. The wiring is intact, just a bit "irritable."
            </p>
            <h3 className="mt-4 fw-semibold">The ALS Twitch: Cell Death and Disorganized Firing</h3>
            <p>
              In ALS, the motor neurons are actively degenerating and dying through a process called apoptosis, or programmed cell death. As the nerve cell becomes sick, its ability to maintain its internal electrical balance fails. It becomes unstable and fires off random, chaotic signals as it loses its connection to the muscle. These are the fasciculations seen in ALS.
            </p>
            <p>
              Using the car alarm analogy, this isn't an over-sensitive system; it's a system with wires that are cut and short-circuiting as the car itself is being dismantled. The alarm blares erratically *because* the system is failing. Most importantly, when the brain sends a command down that dying nerve, the signal is weak or doesn't arrive at all. The result is profound muscle weakness and, eventually, paralysis. The twitch is a secondary sign of this catastrophic failure.
            </p>

            <img
              src="https://images.pexels.com/photos/7089017/pexels-photo-7089017.jpeg"
              alt="A neurologist performing a clinical strength test on a patient's leg, the gold standard for ruling out ALS."
              className="img-fluid rounded my-4"
            />

            <h2 className="mt-5 mb-3 fw-bold">Part 3: The Psychology of Fear – Why Twitching Feeds Anxiety</h2>
            <p>
              It's impossible to discuss BFS without addressing the immense psychological burden it carries. The physical symptom (the twitch) is often less debilitating than the mental symptom (the fear). This is health anxiety, and it operates in a vicious, self-sustaining cycle.
            </p>
            <h3 className="mt-4 fw-semibold">The Hypervigilance Loop</h3>
            <p>
              Once the fear of ALS is planted, your brain enters a state of high alert. You become hyper-aware of every sensation in your body. A normal muscle cramp, a moment of fatigue, a slight stumble—things you would have ignored before—are now scrutinized as potential "new symptoms." You start compulsively checking your muscles, looking for atrophy or weakness. This constant monitoring and self-testing does two things: it creates immense mental stress, and the physical act of constantly flexing and testing your muscles can cause more fatigue and twitching.
            </p>
            <p>
              The stress from this loop floods your body with cortisol and adrenaline, the very hormones that make your already-irritable nerves even more likely to fire. The result? The more you worry about twitching, the more you twitch. The more you twitch, the more you worry. It's a perfect, miserable feedback loop.
            </p>

            <h3 className="mt-4 fw-semibold">Breaking the Cycle</h3>
            <p>
              Breaking free requires a conscious effort to shift your focus from the symptom to the cause of the anxiety. After a clean bill of health from a doctor, the work begins:
            </p>
            <ul>
                <li><strong>Radical Acceptance:</strong> Acknowledge the twitches are there, but reframe them as a harmless annoyance, like tinnitus or eye floaters. Give them no power.</li>
                <li><strong>Stop the Self-Testing:</strong> Make a commitment to stop the constant strength tests. Your ability to walk to the kitchen is all the proof you need that your legs are strong.</li>
                <li><strong>Starve the Fear:</strong> Limit or completely stop searching for symptoms online. You will only find information that feeds your confirmation bias.</li>
                <li><strong>Engage in Stress Reduction:</strong> Actively practice mindfulness, meditation, deep breathing, or gentle exercise. These activities are proven to calm the sympathetic nervous system, reducing the adrenaline that fuels the twitches.</li>
            </ul>

            <h2 className="mt-5 mb-3 fw-bold">Part 4: The Definitive Clinical Comparison</h2>
            <p>
              To a neurologist, the clinical picture of BFS and early ALS are worlds apart. It's not a subtle diagnosis they struggle with; the signs point in completely opposite directions.
            </p>
            {/* ... Table from previous version ... */}
            <Table striped bordered hover className="my-4">
              <thead>
                <tr style={{backgroundColor: '#003f5c', color: 'white'}}>
                  <th>Feature</th>
                  <th>Benign Fasciculation Syndrome (BFS)</th>
                  <th>Amyotrophic Lateral Sclerosis (ALS)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Primary Symptom</strong></td>
                  <td>Twitching is the main event. It is the symptom that causes distress.</td>
                  <td><strong>Progressive weakness and functional loss</strong> is the main event. E.g., "I can no longer lift my coffee cup."</td>
                </tr>
                <tr>
                  <td><strong>Muscle Status</strong></td>
                  <td>Occurs in <strong>strong, healthy muscles</strong> that function normally despite feeling fatigued.</td>
                  <td>Occurs in <strong>weak, failing muscles</strong> that are visibly wasting away (atrophy).</td>
                </tr>
                <tr>
                  <td><strong>Nature of Twitching</strong></td>
                  <td>Migratory and chaotic. Calves one day, eyelids the next, then back or arms. Random and widespread.</td>
                  <td>Often starts focally in a region of weakness and spreads contiguously as weakness progresses to adjacent body parts.</td>
                </tr>
                <tr>
                  <td><strong>Sensory Symptoms</strong></td>
                  <td>Often accompanied by sensory symptoms like tingling, buzzing, or pins and needles (paresthesia).</td>
                  <td>Purely a motor disease. Sensation is almost always completely spared.</td>
                </tr>
                 <tr>
                  <td><strong>Progression</strong></td>
                  <td>Symptoms may wax and wane for years with no change in strength. It does not progress.</td>
                  <td>Symptoms are relentless and progressive, with measurable loss of function over weeks and months.</td>
                </tr>
                <tr>
                  <td><strong>Clinical Exam</strong></td>
                  <td>Neurological exam is completely normal ("clean"). Strength and reflexes are intact.</td>
                  <td>Neurological exam reveals objective weakness, atrophy, and changes in reflexes (often hyperreflexia).</td>
                </tr>
              </tbody>
            </Table>

            <InlineOptIn source="calf-twitching-als" offer="guidelines" />
            <h2 className="mt-5 mb-3 fw-bold">Part 5: What Really Happens During a Neurological Exam?</h2>
            <p>
              The clinical neurological exam is the single most powerful tool for differentiating BFS from ALS. Your anxiety may tell you that you can "hide" weakness or that the doctor will miss something. This is not the case. Here is what they are actually looking for:
            </p>
            <ol>
              <li><strong>Strength Testing:</strong> This isn't a casual push. A neurologist will isolate specific muscle groups and test them against resistance. They are trained to detect even subtle weakness. They use a 0-5 scale, where 5/5 is normal strength. In BFS, you will be 5/5 strength everywhere, even if it feels difficult for you. In ALS, there will be measurable weakness (e.g., 4/5 or less) in affected areas.</li>
              <li><strong>Reflex Testing:</strong> Tapping your knee with a reflex hammer checks the integrity of the spinal cord circuits. In BFS, reflexes are typically normal (2+). They may be slightly brisk (3+) if you are anxious, which is a normal physiological response. In ALS, reflexes are often pathologically brisk (hyperreflexia) due to the loss of inhibition from the brain.</li>
              <li><strong>Pathological Reflexes:</strong> The doctor will check for reflexes that should not be present in an adult, like the Babinski sign (scraping the sole of the foot). The presence of these signs points towards an upper motor neuron issue, a hallmark of ALS. These are absent in BFS.</li>
              <li><strong>Gait and Coordination:</strong> The doctor will watch you walk. Then, they will ask you to walk on your heels. This simple test is extremely sensitive for "foot drop," an early sign of ALS where the tibialis anterior muscle is too weak to lift the foot. The ability to heel-walk is profound evidence against ALS. The same is true for toe-walking to test calf strength.</li>
            </ol>
            <p>
              A clean clinical exam by a neurologist is the gold standard. In the vast majority of cases, it is sufficient to confidently diagnose BFS and completely rule out ALS.
            </p>
            
            <h2 className="mt-5 mb-3 fw-bold">Part 6: Decoding the Tests – A Guide to EMG/NCS</h2>
            <p>
              If a neurologist wants to gather more data, they may order an Electromyography (EMG) and Nerve Conduction Study (NCS). The thought of this test often causes immense fear. Let's demystify it.
            </p>
            <Card className="mb-3">
              <Card.Body>
                <Card.Title className="fw-bold">Nerve Conduction Study (NCS)</Card.Title>
                <Card.Text>
                  This part feels like small static shocks. The doctor places electrodes on your skin over a nerve and uses a stimulator to send a small electrical pulse down the nerve. It measures the speed and strength of the signal. In both BFS and ALS, the NCS is typically <strong>normal</strong> because ALS primarily affects the motor neuron cell body, not the axon's ability to conduct a signal.
                </Card.Text>
              </Card.Body>
            </Card>
            <Card>
              <Card.Body>
                <Card.Title className="fw-bold">Electromyography (EMG)</Card.Title>
                <Card.Text>
                  This is the "needle test." A very thin needle electrode is inserted into specific muscles to "listen" to their electrical activity. This is where the difference becomes crystal clear:
                  <ul>
                    <li><strong>In BFS:</strong> At rest, the EMG needle might pick up "benign fasciculation potentials." They have a specific, healthy shape and firing rate. When you contract the muscle, the EMG shows a normal "interference pattern," meaning the brain can perfectly recruit all the healthy motor units.</li>
                    <li><strong>In ALS:</strong> At rest, the needle finds widespread, abnormal spontaneous activity called <strong>"fibrillation potentials"</strong> and <strong>"positive sharp waves."</strong> These are the electrical sounds of muscle fibers that have lost their nerve supply. This is the hallmark of active denervation and is <strong>not seen in BFS</strong>. During contraction, the patterns are abnormal ("reduced recruitment"), showing that motor units are dying off and the healthy ones are working too hard to compensate.</li>
                  </ul>
                  An EMG can definitively tell the difference between a healthy, irritable nerve and a sick, dying one. A "clean" EMG is the ultimate reassurance.
                </Card.Text>
              </Card.Body>
            </Card>

            <h2 className="mt-5 mb-3 fw-bold">Part 7: Conclusion – It's Time to Trust Your Strength</h2>
            <p>
              We have journeyed deep into the science that governs your symptoms. The evidence is clear and overwhelming: the gap between benign calf twitching and the presentation of ALS is not a fine line; it is a vast chasm.
            </p>
            <p>
              Your twitches are real. Your fatigue may be real. But your strength is also real. Your ability to walk, stand, and move is the objective truth that overrides the subjective noise of twitching muscles and anxious thoughts. ALS steals this ability. BFS does not.
            </p>
            <p>
              The path forward involves a crucial shift in focus. You must pivot from fearing your twitches to managing the underlying hyperexcitability of your nervous system—by addressing stress, improving sleep, and balancing your nutrition. But most importantly, it requires you to actively combat the health anxiety that has taken root. You are not your twitches. You are a person with a strong, healthy body and a nervous system that is simply being a bit too loud. It's time to believe in the science, trust in your strength, and reclaim your peace of mind.
            </p>

            {/* ========================================================== */}
{/* SCIENTIFIC REFERENCES */}
{/* ========================================================== */}
<h2 className="mt-5 mb-3 fw-bold">Scientific References</h2>
<p className="small text-muted">
  Peer-reviewed and authoritative sources supporting key points (benign calf fasciculations, common triggers like exercise/electrolytes/caffeine/sleep, BFS prognosis, and BFS vs. ALS differentiation).
</p>
<ol className="small">
  <li className="mb-2">
    Blexrud MD, Windebank AJ, Daube JR. Long-term follow-up of 121 patients with benign fasciculations. <em>Ann Neurol</em>. 1993;34(4):622-625.<br/>
    <a href="https://pubmed.ncbi.nlm.nih.gov/8215252/" target="_blank" rel="noopener noreferrer">PubMed</a>
  </li>
  <li className="mb-2">
    Mills KR. Characteristics of fasciculations in amyotrophic lateral sclerosis and the benign fasciculation syndrome. <em>Brain</em>. 2010;133(11):3458-3469.<br/>
    <a href="https://academic.oup.com/brain/article/133/11/3458/317786" target="_blank" rel="noopener noreferrer">Oxford Academic</a>
  </li>
  <li className="mb-2">
    Turner MR, Talbot K. Mimics and chameleons in motor neurone disease. <em>Pract Neurol</em>. 2013;13(3):153-164.<br/>
    <a href="https://pn.bmj.com/content/13/3/153" target="_blank" rel="noopener noreferrer">BMJ Practical Neurology</a>
  </li>
  <li className="mb-2">
    Fermont J, Matthews E, Barber M, et&nbsp;al. Prevalence and distribution of fasciculations in healthy adults. <em>Clin Neurophysiol</em>. 2010;121(4):548-553.<br/>
    <a href="https://pubmed.ncbi.nlm.nih.gov/19533451/" target="_blank" rel="noopener noreferrer">PubMed</a>
  </li>
  <li className="mb-2">
    Czell D, Schreckenberger M, et&nbsp;al. Distribution of fasciculations in healthy adults after exercise. <em>Muscle Nerve</em>. 2016;54(1):132-135.<br/>
    <a href="https://pubmed.ncbi.nlm.nih.gov/26789013/" target="_blank" rel="noopener noreferrer">PubMed</a>
  </li>
  <li className="mb-2">
    Hart IK, Maddison P, Newsom-Davis J, Vincent A, Mills KR. Phenotypic variants of autoimmune peripheral nerve hyperexcitability. <em>Brain</em>. 2002;125(8):1887-1895.<br/>
    <a href="https://pubmed.ncbi.nlm.nih.gov/12135978/" target="_blank" rel="noopener noreferrer">PubMed</a>
  </li>
  <li className="mb-2">
    Filippakis A, Jara J, Ventura N, et&nbsp;al. A prospective study of benign fasciculation syndrome and anxiety. <em>Muscle Nerve</em>. 2018;58(6):852-854.<br/>
    <a href="https://pubmed.ncbi.nlm.nih.gov/30028521/" target="_blank" rel="noopener noreferrer">PubMed</a>
  </li>
  <li className="mb-2">
    Gragossian A, Bashir K, Friede R. <em>Hypomagnesemia</em>. StatPearls [Internet]. Treasure Island (FL): StatPearls Publishing; updated 2025. (Electrolyte imbalance → neuromuscular irritability, cramps/fasciculations).<br/>
    <a href="https://www.ncbi.nlm.nih.gov/books/NBK500003/" target="_blank" rel="noopener noreferrer">NCBI Bookshelf</a>
  </li>
  <li className="mb-2">
    Drake CL, Roehrs T, Shambroom J, Roth T. Caffeine effects on sleep taken 0, 3, or 6 hours before bedtime. <em>J Clin Sleep Med</em>. 2013;9(11):1195-1200.<br/>
    <a href="https://pubmed.ncbi.nlm.nih.gov/24235903/" target="_blank" rel="noopener noreferrer">PubMed</a>
  </li>
  <li className="mb-2">
    Chang A-M, Aeschbach D, Duffy JF, Czeisler CA. Evening use of light-emitting eReaders negatively affects sleep, circadian timing, and next-morning alertness. <em>Proc Natl Acad Sci U S A</em>. 2015;112(4):1232-1237.<br/>
    <a href="https://www.pnas.org/doi/10.1073/pnas.1418490112" target="_blank" rel="noopener noreferrer">PNAS</a>
  </li>
  <li className="mb-2">
    Allen RP, Picchietti DL, et&nbsp;al. Restless legs syndrome/Willis-Ekbom disease diagnostic criteria: updated International RLS Study Group consensus criteria—history, rationale, description, and significance. <em>Sleep Med</em>. 2014;15(8):860-873.<br/>
    <a href="https://pubmed.ncbi.nlm.nih.gov/25023924/" target="_blank" rel="noopener noreferrer">PubMed</a>
  </li>
</ol>

            
            <hr className="my-5" />

            <div className="text-center">
              <Link
                to="/blog"
                className="btn btn-primary px-4"
                style={{
                  backgroundColor: '#003f5c',
                  borderColor: '#003f5c',
                  borderRadius: '9999px',
                  fontWeight: '600',
                }}
              >
                Back to All Articles
              </Link>
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default BlogPostCalfTwitchingALS;
